// The 10 to 40 second wait while gpt-4o-mini drafts a document: an
// indeterminate linear progress (UI Core Progress) with visible text naming
// the work and the elapsed seconds, and the 502 recovery notice. Only real
// facts are shown: no invented stage names, no fake percentage.
import { useEffect, useState, type ReactNode } from "react";
import { ApiError } from "../api";
import { Notice } from "./components";

export function useElapsed(running: boolean): number {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (!running) return;
    setSeconds(0);
    const started = Date.now();
    const t = setInterval(() => setSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(t);
  }, [running]);
  return seconds;
}

export function GenerationProgress({ what, seconds, outline }: { what: string; seconds: number; outline?: string[] }) {
  const late = seconds >= 45;
  return (
    <div className="gen-progress" role="status" aria-live="polite">
      <p className="gen-title">Menyusun draf {what}</p>
      <div className="ui-progress">
        <progress aria-label={`Menyusun draf ${what}`} />
      </div>
      <p className="gen-meta">
        <span className="num">{seconds}</span> detik berjalan.{" "}
        {late ? "Masih diproses, mohon tunggu sebentar lagi." : "Biasanya 10 sampai 40 detik. Jangan tutup halaman ini."}
      </p>
      {outline && outline.length > 0 ? (
        <div className="gen-outline">
          <p className="card-subtitle">Bagian yang sedang disusun</p>
          <ol className="preview-outline">
            {outline.map((o) => (
              <li key={o}>{o}</li>
            ))}
          </ol>
          <p className="preview-note">Setelah selesai, draf terbuka di halaman dokumen untuk Anda periksa.</p>
        </div>
      ) : null}
    </div>
  );
}

export function GenerationError({ error, onRetry, retryLabel = "Coba lagi", secondary }: { error: unknown; onRetry: () => void; retryLabel?: string; secondary?: ReactNode }) {
  const aiDown = error instanceof ApiError && error.status === 502;
  // 429: the shared AI budget (contract 8.5), in the chat's own words.
  const limited = error instanceof ApiError && error.status === 429;
  const message = error instanceof ApiError ? error.message : "Tidak dapat terhubung ke server. Periksa koneksi lalu coba lagi.";
  return (
    <Notice
      tone={limited ? "warning" : "danger"}
      title={aiDown ? "Layanan Pluto sedang tidak tersedia" : limited ? "Batas penggunaan Pluto tercapai" : "Draf belum berhasil dibuat"}
      action={
        <div className="button-row">
          <button type="button" className="btn" onClick={onRetry}>
            {retryLabel}
          </button>
          {secondary}
        </div>
      }
    >
      {aiDown ? <p>Draf belum dibuat. Coba lagi dalam beberapa saat.</p> : <p>{message}</p>}
      <p>Isian Anda masih tersimpan di halaman ini, jadi Anda cukup mencoba lagi.</p>
    </Notice>
  );
}
